import React from 'react';
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import {StatementDownloadSessionStatusDto} from "../../model/statement/autofetch/StatementDownloadSessionStatusDto";
import {LhvConnectApi} from "../../backend/LhvConnectApi";
import {Accordion, AccordionDetails, AccordionSummary, Alert, Divider, Grid, Typography} from "@mui/material";
import moment, {Moment} from "moment";
import LoadingButton from "../../components/button/LoadingButton";
import {WiseConnectionApi} from "../../backend/WiseConnectionApi";
import {DatePicker} from "../../components/picker/DatePicker";

interface IProps {
    api: LhvConnectApi | WiseConnectionApi
}

interface IState {
    loading: boolean,
    error: boolean,
    status?: StatementDownloadSessionStatusDto,
    from: Moment,
    to: Moment
}

export default class StatementDownloadLog extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            from: moment().subtract(2, 'days'),
            to: moment().subtract(1, 'day')
        };
    }

    async componentDidMount() {
        this.props.api.fetchStatementProcessingStatus()
                .then((data) => {
                    this.setState({loading: false, status: data})
                })
                .catch(() => this.setState({
                    loading: false,
                    error: true
                }));
    }

    render() {
        const {loading, error, status, from, to} = this.state;
        return <LoaderWithCompanySelected loading={loading} error={error}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {status?.status && this.renderLogsSection(status)}
                    {!status?.status && <Typography>No status updates</Typography>}
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton variant="contained" onClick={this.refreshStatus} refreshInterval={30}>
                        Refresh status
                    </LoadingButton>
                </Grid>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                <Grid container item xs={12} spacing={1} alignItems={"flex-end"}>
                    <Grid item>
                        <DatePicker
                                disableFuture
                                label="From"
                                value={from}
                                onChange={date => this.handleFromDateChange(date!!)}
                        />
                    </Grid>
                    <Grid item>
                        <DatePicker
                                disableFuture
                                label="To"
                                value={to}
                                onChange={date => this.handleToDateChange(date!!)}
                        />
                    </Grid>
                    <Grid item>
                        <LoadingButton variant="contained" onClick={this.doTriggerDownload}>
                            Download statements
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Grid>
        </LoaderWithCompanySelected>
    }

    private renderLogsSection(status: StatementDownloadSessionStatusDto) {
        const isError = StatementDownloadLog.isErrorStatus(status);
        return <>
            <Alert severity={isError ? "error" : "info"}>
                {status.status}
                {status.error && <>
                    <Divider/>
                    {status.error}
                </>}
            </Alert>
            <Accordion expanded={true}>
                <AccordionSummary>
                    <Typography>Logs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container>
                        {status.logs.map((it, index) => {
                            return <Grid item xs={12} key={index}>
                                <Typography variant={"body2"}>{it}</Typography>
                            </Grid>
                        })}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>;
    }

    private static isErrorStatus(status?: StatementDownloadSessionStatusDto) {
        if (status) {
            return status.status === 'ERROR' || status.status === 'INTERRUPTED';
        } else {
            return false;
        }
    }

    private doTriggerDownload = async () => {
        await this.props.api.startStatementDownload(this.state.from.toDate(), this.state.to.toDate())
        await this.refreshStatus();
    }

    private refreshStatus = async () => {
        this.setState({status: await this.props.api.fetchStatementProcessingStatus()})
    }

    private handleFromDateChange = (newDate: Moment) => {
        if (newDate) {
            if (newDate >= this.state.to) {
                this.setState({from: newDate, to: newDate.clone().add(1, 'day')})
            } else {
                this.setState({from: newDate})
            }
        }
    };

    private handleToDateChange = (newDate: Moment) => {
        if (newDate) {
            if (newDate <= this.state.from) {
                this.setState({to: newDate, from: newDate.clone().subtract(1, 'day')})
            } else {
                this.setState({to: newDate})
            }
        }
    };
}